import transformImage from '../transform/transformImage';

export type ContributorNodeBase = {
  databaseId: number;
  title?: string;
  slug: string;
  uri: string;
  contributorsCategories?: { nodes: { name: string }[] };
  highlightFields?: { type?: string; items?: string[] | null }[];
  featuredImage?: {
    node: { altText?: string; publicId?: string; sourceUrl?: string };
  };
};

const CANONICAL_ROLES = ['writer', 'editor', 'reviewer'] as const;

export function resolveRoles(node: ContributorNodeBase): string[] {
  const categories = node.contributorsCategories?.nodes ?? [];
  if (categories.length === 0) return ['Writer'];

  const roles: string[] = [];
  for (const cat of categories) {
    const lower = (cat.name ?? '').toLowerCase();
    let matched = false;
    for (const canonical of CANONICAL_ROLES) {
      if (lower.includes(canonical)) {
        const label = canonical.charAt(0).toUpperCase() + canonical.slice(1);
        if (!roles.includes(label)) roles.push(label);
        matched = true;
        break;
      }
    }
    if (!matched && cat.name) roles.push(cat.name);
  }
  return roles.length > 0 ? roles : ['Writer'];
}

export type ContributorHighlightSnippet = {
  type: string;
  text: string;
};

export function resolveHighlightSnippet(
  fields: { type?: string; items?: string[] | null }[] | undefined,
): ContributorHighlightSnippet | undefined {
  if (!fields?.length) return undefined;

  const expertiseField = fields.find(
    (f) => f.type && /expertise|subject|specialty/i.test(f.type),
  );
  const sourceField =
    expertiseField && (expertiseField.items?.length ?? 0) > 0
      ? expertiseField
      : fields.find((f) => (f.items?.length ?? 0) > 0);

  if (!sourceField?.type) return undefined;

  const text = (sourceField.items ?? []).filter(Boolean).join(', ');
  if (!text) return undefined;

  return { type: sourceField.type, text };
}

export function resolveContributorImage(
  node: ContributorNodeBase,
): string | undefined {
  const publicId = node.featuredImage?.node?.publicId ?? '';
  const srcUrl = node.featuredImage?.node?.sourceUrl;
  return transformImage(publicId, {}, srcUrl) ?? undefined;
}
